import { useRef } from "react";
import * as StateLib from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { enumDataSource, enumFeaturesList, enumPlanStatus } from "@coreapp/constants";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFeatureAccess } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";

export const useAutoSyncHandler = ({ doc }) => {
  const nav = useAppNavigate();
  const { isRefreshable } = useChartDocumentContext();
  const { planManager } = usePlanManagerContext();

  const iWiz = doc?.GetIWizData();
  const chartID = iWiz?.fBaseChartID;
  const openCtrlRef = useRef(null);

  const handleRefreshClick = () => {
    if (doc.IsPlanChart()) {
      const wsMgr = StateLib.GetWSMgr();
      wsMgr.SyncPlan();
      return;
    }

    if (!doc.IsServerChart()) {
      nav.setMode("edit");
    }
    StateLib.RefreshChart(null);
  };

  const toggleLiveChart = () => {
    iWiz.fIsLiveChart = !iWiz.fIsLiveChart;
    const wsMgr = StateLib.GetWSMgr();
    nav.setMode("edit");
    doc.SetDirty(true);
  };

  const chartDataSource = iWiz?.GetDataSource();
  const disableLiveToggle =
    chartDataSource !== enumDataSource.chartbuilder || !getFeatureAccess(enumFeaturesList.autoSync);
  const docMgr = doc?.GetDocMgr();
  const currentFilename = docMgr?.GetFileName(true);
  const isBackground = currentFilename ? IsBackgroundFile(currentFilename) : false;
  const isArchive = chartDataSource === enumDataSource.pointintime;
  const canGoLive =
    (chartDataSource === enumDataSource.chartbuilder ||
      chartDataSource === enumDataSource.serverchart) &&
    !isBackground;

  let autoSyncEnabled = iWiz?.fIsLiveChart;
  if (chartDataSource === enumDataSource.serverchart) {
    let syncSchedule = iWiz?.fAutoSyncSchedule?.schedule ?? "Off";
    autoSyncEnabled = syncSchedule !== "Off";
  }

  let refreshHoverTip = "datarefresh";
  if (doc?.IsServerChart()) refreshHoverTip = "Refresh base";
  if (doc?.IsPlanChart()) refreshHoverTip = "Sync plan";

  let lastRefreshed = doc?.GetRefreshTimeStamp();
  let preventRefresh = !isRefreshable;
  if (!doc?.IsServerChart()) {
    preventRefresh = false;
  }

  let isPlanSyncDisabled = false;
  let planSyncDisabledTooltip = null;
  if (doc?.IsPlanChart()) {
    const planMgr = planManager?.state;
    const planStatus = planMgr?.GetPlanStatus();

    if (planStatus === enumPlanStatus.locked) {
      isPlanSyncDisabled = true;
      planSyncDisabledTooltip = appstr("Locked plans cannot be synced with base chart data");
    } else if (planStatus === enumPlanStatus.archived) {
      isPlanSyncDisabled = true;
      planSyncDisabledTooltip = appstr("Archived plans cannot be synced with base chart data");
    }
    preventRefresh = !planMgr?.CanUpdatePlan();
  }

  const isCustomBackgroundDirty =
    !doc?.IsServerChart() && (StateLib.GetWSMgr()?.IsBackgroundDirty() ?? false);
  const backgroundDirtyTooltip = isCustomBackgroundDirty
    ? appstr("Cannot sync with unsaved background changes")
    : null;

  const showRefresh = !isBackground;
  const disableManualRefresh =
    isArchive ||
    !showRefresh ||
    Globals._uiConfig.InBackgroundEditorMode() ||
    isCustomBackgroundDirty ||
    preventRefresh ||
    !getFeatureAccess(enumFeaturesList.refresh);

  return {
    openCtrlRef,
    disableManualRefresh,
    showRefresh,
    lastRefreshed,
    autoSyncEnabled,
    canGoLive,
    disableLiveToggle,
    refreshHoverTip,
    toggleLiveChart,
    handleRefreshClick,
    isPlanSyncDisabled,
    planSyncDisabledTooltip,
    isCustomBackgroundDirty,
    backgroundDirtyTooltip
  };
};
